/**
 * The HOC version of PageDisplay.
 *
 * This HOC updates the page settings and also injects a `updatePageDisplay`
 * prop to the wrapped component.
 *
 * WARNING: This prop is meant to be used inside the lifecycle methods
 * (compomentDidMount, componentDidUpdate, etc) or other
 * user-interaction-related methods (onClick, etc). Use it with caution in case
 * you need to use it inside the render() method. Consider using `PageDisplay`
 * instead.
 *
 *  Examples:
 * - export default withPageDisplay({title: "About"})(WrappedComponent);
 * - export default compose(
 *      withPageDisplay({title: "About"}),
 *      withRouter
 *   )(WrappedComponent);
 *
 * It also allows us to disable resetting page settings for the current
 * component. This means, that if we pass `resetSettings=false`, we will only
 * update the settings that we have initially passed in the HOC instance and
 * these settings will remain the same inside this component (unless overriden
 * using `updatePageDisplay`).
 *
 * The pattern described above can be done by setting the second argument
 * defined in `withPageDisplay`.
 *
 * Example:
 * export default withPageDisplay({title: "About"}, false)(WrappedComponent);
 *
 * NOTE: `resetSettings` is true by default.
 */

import * as React from "react";

import {PageDisplayContext} from "./page-display-container.tsx";
import type {PartialPageSettings} from "./page-settings.ts";

export type WithPageDisplayProps = {
    updatePageDisplay: (settings: PartialPageSettings, reset?: boolean) => void;
};

export type WithoutPageDisplay<T> = Omit<T, keyof WithPageDisplayProps>;

// TODO (FEI-4145) Reimplement this with usePageSettings + useUpdatePageSettings
const withPageDisplay =
    (
        // The page settings we want to use
        settings: PartialPageSettings,
        // Whether we want to reset the current page settings
        resetSettings = true,
    ) =>
    <
        C extends React.ComponentType<
            React.ComponentProps<C> & WithPageDisplayProps
        > & {
            displayName?: string | undefined;
        },
    >(
        WrappedComponent: C,
    ) => {
        type OuterProps = Omit<
            JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
            keyof WithPageDisplayProps
        >;

        // Try to create a nice displayName for React Dev Tools.
        const displayName =
            WrappedComponent.displayName ||
            WrappedComponent.name ||
            "Component";

        // Creating the inner component. The calculated Props type here is the where the magic happens.
        const ComponentWithApollo = (props: OuterProps) => {
            return (
                <PageDisplayContext.Consumer>
                    {({update}) => {
                        update(settings, resetSettings);
                        return (
                            <WrappedComponent
                                {...(props as any)}
                                updatePageDisplay={update}
                            />
                        );
                    }}
                </PageDisplayContext.Consumer>
            );
        };

        ComponentWithApollo.displayName = `withPageDisplay(${displayName})`;

        return ComponentWithApollo;
    };

export default withPageDisplay;
